import { AGENT_IDS, PERSONAS, shortAddr } from '@quintessence/core';
import { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { addrUrl, brand, tokenAddress, useSnapshot } from '../lib';
import { BrandLogo } from './icons';
import { BRAND, SERIES } from '../theme';

/** Quincunx mark: the four element agents wired to the fifth, plus the protocol's wordmark. */
export function Logo() {
  const { config } = useSnapshot();
  const corners = [
    { id: 'pyre', x: 3.5, y: 3.5 },
    { id: 'hoard', x: 16.5, y: 3.5 },
    { id: 'rain', x: 3.5, y: 16.5 },
    { id: 'tide', x: 16.5, y: 16.5 },
  ] as const;
  return (
    <span className="logo">
      <svg className="logo-mark" width="20" height="20" viewBox="0 0 20 20" aria-hidden>
        {corners.map((c) => (
          <line key={c.id} x1={c.x} y1={c.y} x2="10" y2="10" />
        ))}
        {corners.map((c) => (
          <circle key={c.id} cx={c.x} cy={c.y} r="2" fill={BRAND[c.id]} />
        ))}
        <circle className="logo-core" cx="10" cy="10" r="2.8" />
      </svg>
      {brand(config).name}
    </span>
  );
}

const SECTIONS = [
  { id: 'how', label: 'Protocol' },
  { id: 'agents', label: 'Agents' },
  { id: 'intelligence', label: 'Intelligence' },
  { id: 'maths', label: 'Maths' },
  { id: 'revenue', label: 'Roadmap' },
];

export function Nav() {
  const { config, kind } = useSnapshot();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onLanding = pathname === '/';
  const close = () => setOpen(false);
  const live = kind === 'live' && config.mode === 'live';
  return (
    <header className="nav">
      <div className="nav-inner">
        <Link to="/" className="nav-brand" onClick={close}>
          <Logo />
        </Link>
        <nav className={`nav-links ${open ? 'open' : ''}`} aria-label="Primary">
          {SECTIONS.map((s) => (
            <a key={s.id} href={onLanding ? `#${s.id}` : `/#${s.id}`} onClick={close}>
              {s.label}
            </a>
          ))}
          <NavLink to="/dashboard" onClick={close}>
            Dashboard
          </NavLink>
        </nav>
        <div className="nav-end">
          {/* A runtime in sim mode serves real agent data over a simulated market: label it as such. */}
          <span
            className="nav-status"
            title={kind === 'demo' ? 'Simulated market in your browser' : live ? `Live on ${config.network}` : 'Agent runtime on a simulated market'}
          >
            <i className={live ? 'live' : ''} />
            {kind === 'demo' ? 'Demo' : live ? 'Live' : 'Sim'}
          </span>
          <Link to="/dashboard" className="btn btn-small" onClick={close}>
            Launch app
          </Link>
          <button type="button" className="nav-toggle" aria-expanded={open} aria-label="Menu" onClick={() => setOpen((v) => !v)}>
            <span />
            <span />
          </button>
        </div>
      </div>
    </header>
  );
}

export function Footer() {
  const { config } = useSnapshot();
  const { pathname } = useLocation();
  const width = pathname === '/' ? 'container' : 'wide';
  const { name, symbol } = brand(config);
  const bots = AGENT_IDS.filter((id) => config.socials.telegramBots[id]);
  const vaults = AGENT_IDS.filter((id) => config.contracts.vaults[id]);
  const token = tokenAddress(config);
  const hasSocials = Boolean(config.socials.x || config.socials.telegram || bots.length);
  const anchor = (id: string) => (pathname === '/' ? `#${id}` : `/#${id}`);
  return (
    <footer className="footer">
      <div className={`${width} footer-grid`}>
        <div className="footer-about">
          <Logo />
          <p>
            Four autonomous agents turning {symbol} trading fees into burns, buybacks, airdrops and locked liquidity on {config.network}. Every action is on-chain
            and posted publicly.
          </p>
        </div>
        <div>
          <h4>Protocol</h4>
          <ul className="footer-links">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a href={anchor(s.id)}>{s.id === 'how' ? 'How it works' : s.label}</a>
              </li>
            ))}
            <li>
              <Link to="/dashboard">Dashboard</Link>
            </li>
          </ul>
        </div>
        <div>
          <h4>Community</h4>
          <div className="socials">
            <SocialLink href={config.socials.x} label="X · all four agents" logo="x" />
            <SocialLink href={config.socials.telegram} label="Telegram community" logo="telegram" />
          </div>
          <ul className="footer-links">
            {bots.map((id) => (
              <li key={id} className="footer-bot">
                <i className="dot" style={{ background: SERIES[id] }} aria-hidden />
                {PERSONAS[id].name} · @{config.socials.telegramBots[id]}
              </li>
            ))}
            {!hasSocials && <li className="muted">Channels open at launch.</li>}
          </ul>
        </div>
        <div>
          <h4>Contracts</h4>
          <ul className="footer-links mono">
            <li>
              Token{' '}
              <a href={addrUrl(config, token)} target="_blank" rel="noreferrer">
                {shortAddr(token)}
              </a>
            </li>
            {config.contracts.treasury && (
              <li>
                Treasury{' '}
                <a href={addrUrl(config, config.contracts.treasury)} target="_blank" rel="noreferrer">
                  {shortAddr(config.contracts.treasury)}
                </a>
              </li>
            )}
            {vaults.map((id) => (
              <li key={id}>
                {PERSONAS[id].name}{' '}
                <a href={addrUrl(config, config.contracts.vaults[id]!)} target="_blank" rel="noreferrer">
                  {shortAddr(config.contracts.vaults[id]!)}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className={`${width} footer-base`}>
        <span>
          {name} · Chain {config.chainId} · {config.network}
        </span>
        <span>Not financial advice. Coins are volatile and can go to zero.</span>
      </div>
    </footer>
  );
}

/** A round logo button; inert (and dimmed) until the channel's URL is configured. */
function SocialLink({ href, label, logo }: { href: string | null; label: string; logo: 'x' | 'telegram' }) {
  if (!href) {
    return (
      <span className="social social-soon" role="img" aria-label={`${label}, opens at launch`} title={`${label}: opens at launch`}>
        <BrandLogo name={logo} size={17} />
      </span>
    );
  }
  return (
    <a className="social" href={href} target="_blank" rel="noreferrer" aria-label={label} title={label}>
      <BrandLogo name={logo} size={17} />
    </a>
  );
}

/** Thin neutral strip that says when the data is simulated, with the demo's warm-up and speed controls. */
export function DemoBar() {
  const { kind, status, warmup, speed, setSpeed, notice, config } = useSnapshot();
  if (kind === 'live' && config.mode === 'sim') {
    return (
      <div className="demo-bar" role="status">
        <div className="wide demo-inner">
          <span className="demo-label">
            <i className="demo-dot" />
            <span>
              Simulation<span className="demo-long"> · real agent brains on a simulated market</span>
            </span>
          </span>
        </div>
      </div>
    );
  }
  if (kind !== 'demo') return null;
  const warming = status === 'warming' || status === 'connecting';
  const pct = Math.round((warmup?.progress ?? 0) * 100);
  return (
    <div className="demo-bar" role="status">
      <div className="wide demo-inner">
        <span className="demo-label">
          <i className="demo-dot" />
          <span>
            Demo · simulated market<span className="demo-long"> running in your browser</span>
          </span>
        </span>
        {warming ? (
          <span className="demo-progress">
            Training the brains · day {warmup?.day ?? 1} of {warmup?.days ?? 10}
            <span className="meter" aria-hidden>
              <span style={{ width: `${pct}%` }} />
            </span>
            {pct}%
          </span>
        ) : (
          <span className="demo-speed">
            Speed
            {[1, 60, 600].map((s) => (
              <button key={s} type="button" className={speed === s ? 'active' : ''} onClick={() => setSpeed(s)} aria-pressed={speed === s}>
                {s}×
              </button>
            ))}
          </span>
        )}
        {notice && <span className="demo-notice">{notice}</span>}
      </div>
    </div>
  );
}

export function RegimeBadge({ name }: { name: string }) {
  const tone: Record<string, string> = {
    Euphoria: 'up',
    Uptrend: 'up',
    Chop: 'flat',
    Quiet: 'flat',
    Bleed: 'down',
    Capitulation: 'down',
  };
  return (
    <span className={`regime-badge ${tone[name] ?? 'flat'}`}>
      <i aria-hidden /> Regime <strong>{name}</strong>
    </span>
  );
}
